import type {ShopperCustomers} from 'commerce-api'
import pwaKitConfig from '../pwa-kit.config.json'
import {ErrorWithTextMessage} from '../types/ui'
import {DateUtils} from './date-utils'
import BonusCardApp from './app'
import { Hit, Promotion } from '../store/ProductStore'

/**
 * Call requestIdleCallback in supported browsers.
 *
 * https://developers.google.com/web/updates/2015/08/using-requestidlecallback
 * http://caniuse.com/#feat=requestidlecallback
 */
export const requestIdleCallback = (fn: () => void | IdleRequestCallback) => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(fn)
  } else {
    return setTimeout(() => fn(), 1)
  }
}

/**
 * Compares the primary fields of two address objects to determine if they match.
 */
type AddressParams = Partial<ShopperCustomers.OrderAddress & ShopperCustomers.CustomerAddress>
export const isMatchingAddress = (addr1: AddressParams, addr2: AddressParams) => {
  const normalize = (addr: AddressParams) => {
    // eslint-disable-next-line no-unused-vars
    const {id, addressId, _type, preferred, creationDate, lastModified, ...normalized} = addr
    return normalized
  }
  return shallowEquals(normalize(addr1), normalize(addr2))
}

/**
 * Performs a shallow comparison on two objects
 */
export const shallowEquals = (a: Record<string, any>, b: Record<string, any>) => {
  for (const key in a) {
    if (!(key in b) || a[key] !== b[key]) {
      return false
    }
  }
  for (const key in b) {
    if (!(key in a) || a[key] !== b[key]) {
      return false
    }
  }
  return true
}

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => {}

/**
 * Flattens a tree data structure into an array.
 */
export const flatten = (node: Record<string, any>, key = 'children') => {
  const children = (node[key] || []).reduce((a: any, b: any) => {
    return Array.isArray(b[key]) && !!b[key].length ? {...a, ...flatten(b, key)} : {...a, [b.id]: b}
  }, {})

  return {
    [node.id]: node,
    ...children,
  }
}

/**
 * Check the current execution environment
 * is client side or server side
 */
export const isServer = typeof window === 'undefined'

/**
 * Capitalizes the words in a string
 */
export const capitalize = (text: string) => {
  if (!text) return
  return text
    .toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ')
}

/**
 * Converts a string to a URL-friendly slug format.
 * Transforms to lowercase and replaces spaces with hyphens.
 * Optionally removes non-alphanumeric characters except hyphens.
 * 
 * @param text - The text to convert to a slug
 * @param removeSpecialChars - If true, removes all characters except a-z, 0-9, and hyphens
 * @returns The slugified string
 */
export const toSlug = (text: string, removeSpecialChars = false): string => {
  if (!text) return ''
  let slug = text.toLowerCase().replace(/\s+/g, '-')
  if (removeSpecialChars) {
    slug = slug.replace(/[^a-z0-9-]/g, '')
  }
  return slug
}

export const getConfig = () => pwaKitConfig

export const getUrlConfig = () => getConfig().url

function isErrorWithTextMessage(error: unknown): error is ErrorWithTextMessage {
  return (
    typeof error === 'object' &&
    error != null &&
    !!(error as Record<string, unknown>)?.message &&
    typeof (error as Record<string, unknown>)?.message === 'string'
  )
}

export function formatStatusDetails(details: string) {
  // Details should be in the format response={data}
  // This removes the response string and brackets leaving the data to be read
  return details.replace('response={', '').slice(0, -1)
}

/**
 * Checks if string can be parsed as JSON
 * If can be parsed returns the JSON Object if not returns null
 */
export function isJSONString(str: string | null) {
  if (str === null) {
    return str
  }

  try {
    return JSON.parse(str)
  } catch (e) {
    return null
  }
}

/**
 * Given an object (response from email validation API) see if it has
 * a typo property. If it does return a formatted message
 */
export function getPossibleTypo(obj: Record<string, unknown>) {
  if (obj && Object.hasOwn(obj, 'typo') && obj.typo) {
    let msg = 'Please check this value, are you sure this is correct?'
    if (Object.hasOwn(obj, 'typoSuggestion')) {
      msg = `Did you mean? ${obj.typoSuggestion}`
    }
    return msg
  }
  return null
}

export function getErrorMessage(error: unknown, isTroubleLoggingInEmailEnabled?: boolean): string {
  if (isErrorWithTextMessage(error)) return error.message
  if ((error as any).error === 'LoginNotUniqueException') {
    const resetLink = `<a href="/login" style="text-decoration:underline;">reset your password</a>`
    const message = isTroubleLoggingInEmailEnabled
      ? `you’ll receive an e-mail to reset your password`
      : `please ${resetLink}`
    return `Sorry, your account could not be created. If you already have an account, ${message}.`
  }
  return "Something's not right, please check and try again."
}

export function getFormattedDate(dateString: string) {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = (1 + date.getMonth()).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  return `${day}/${month}/${year}`
}

const toCamel = (str: string) => {
  if (str.startsWith('_') || str.startsWith('c_')) {
    return str
  }
  return str.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace('-', '').replace('_', '')
  })
}

const isObject = (obj: any) => {
  return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function'
}

export const keysToCamel = (obj: any): any => {
  if (isObject(obj)) {
    const n: Record<string, any> = {}

    Object.keys(obj).forEach((k) => {
      n[toCamel(k)] = keysToCamel(obj[k])
    })

    return n
  } else if (Array.isArray(obj)) {
    return obj.map((i) => {
      return keysToCamel(i)
    })
  }

  return obj
}

export const getSecondsLeft = (date: string) => {
  const t1 = DateUtils.current()
  const t2 = DateUtils.utcToZonedDate(date)
  return Math.round((t2.getTime() - t1.getTime()) / 1000)
}

// prepend a zero onto a string/number for display (like a date or timer)
export const prependZero = (val: number | string) => (String(val).length < 2 ? '0' + val : val)

// Pointer helps to determine click and click & drag mouse event.
// Can be used with mouseup/mousedown event listeners.
export function Pointer(threshold = 10) {
  let x = 0
  let y = 0

  return {
    start(e: MouseEvent) {
      x = e.clientX
      y = e.clientY
    },

    getCoords() {
      return {x, y}
    },

    reset() {
      x = 0
      y = 0
    },

    isDragEvent(e: MouseEvent) {
      if (x === 0 && y === 0) return false

      const deltaX = Math.abs(e.clientX - x)
      const deltaY = Math.abs(e.clientY - y)

      return deltaX >= threshold || deltaY >= threshold
    },
  }
}

export const escapeStringForRegExp = (str: string) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const validateDate = (date: Date) => {
  return {
    // If the date object is invalid it
    // will return 'NaN' on getTime()
    // and NaN is never equal to itself.
    isValid: date.getTime() === date.getTime(),
  }
}

export const deepCopy = <T, U = T extends Array<infer V> ? V : never>(source: T): T => {
  if (Array.isArray(source)) {
    return source.map((item) => deepCopy(item)) as T & U[]
  }
  if (source instanceof Date) {
    return new Date(source.getTime()) as T & Date
  }
  if (source && typeof source === 'object') {
    return (Object.getOwnPropertyNames(source) as (keyof T)[]).reduce<T>(
      (o, prop) => {
        Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop)!)
        o[prop] = deepCopy(source[prop])
        return o
      },
      Object.create(Object.getPrototypeOf(source)),
    )
  }
  return source
}

/**
 * Utility provides same functionality as Object.keys does,
 * however collection of Object.keys call result doesn't have typed keys: any element will be string,
 * this util can be used to get exact type of object keys during iteration of Object.keys collection.
 */
export const objectKeys = <Obj extends Object>(obj: Obj): (keyof Obj)[] =>
  Object.keys(obj) as (keyof Obj)[]

/**
 * Provides ordinal suffix for day number.
 * Based on Intl.PluralRules.
 */
export const getDayNumberWithOrdinalSuffix = (day: number): string => {
  const suffixes = {
    zero: 'th',
    many: 'th',
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  }
  const ordinalRules = new Intl.PluralRules('en', {type: 'ordinal'})

  const category = ordinalRules.select(day)
  const suffix = suffixes[category]

  return day + suffix
}

/**
 * Get currency symbol based on provided currency and locale (default to en-GB)
 */
export const getCurrencySymbol = (currency: string, locale = 'en-GB') => {
  if (!currency) return ''

  return (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim()
}

//TODO Refactor to use testUserAgent and get rid of this function
/**
 * Format number value to string with correct currency symbol
 * @param value amount to format
 * @param currency currency code default to GBP
 * @param minimumFractionDigits minimum number of decimal places to display
 * @param maximumFractionDigits maximum number of decimal places to display
 * @returns formatted currency string
 */

export function formatNumberToCurrency(value: number | undefined, currency = 'GBP', minimumFractionDigits = 2, maximumFractionDigits = 2) {
  if (!value) return ''
  
  // If the value has a decimal part, ensure we show the maximum decimal places
  const hasDecimal = value % 1 !== 0
  const minDigits = hasDecimal ? maximumFractionDigits : minimumFractionDigits
  
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maximumFractionDigits,
  }).format(value)
}

/**
 * Match user agent to representation of device type.
 * @param userAgentString User Agent string to evaluate
 * @returns
 */
export const getDeviceType = (userAgentString: string | undefined | null) => {
  if (!userAgentString) return

  userAgentString = userAgentString.toLowerCase()

  const iPhoneDevice = 'iphone'
  const iPadDevice = 'ipad'
  const androidDevice = 'android'

  if (userAgentString.includes(iPadDevice)) return 'tablet'

  if (
    (userAgentString.includes(androidDevice) && userAgentString.includes('mobile')) ||
    userAgentString.includes(iPhoneDevice)
  ) {
    return 'mobile'
  }

  return 'desktop'
}

export const isValidVSEDomain = (value: unknown) => {
  return typeof value === 'string' && value.trim().toLowerCase().endsWith('staging.bigcontent.io')
}

// Find out the user device:
//TODO Refactor to use testUserAgent and get rid of this function
export const getMobileDeviceOS = () => {
  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent
    if (/android/i.test(userAgent)) {
      return 'Android'
    }
    if (/iPad|iPhone|iPod|iOS/.test(userAgent)) {
      return 'iOS'
    }
  }
  return 'unknown'
}

// Format URL to remove special characters

export function formatProductName(name: string | undefined) {
  if (name === undefined || name === null) return
  return encodeURI(
    name
      .replace(/ /g, '-') // Replace spaces with hyphens
      .replace(/&/g, 'and') // Replace '&' with 'and'
      .replace(/'/g, '') // Remove apostrophes
      .replace(/\//g, '-') // Replace slashes with hyphens
      .toLowerCase() // change all letters to lowercase
  )
}

// Possible reasons purchase would be disabled:
// 1. global inStock flag is set to false (Handled in transformHits and part of availableforDayAtStore check)
// 2. Product does not have a display price
// 3. Product is not available for sale on the current slot/store combination
// 4. Product is badged with the isAddToCartDisabled flag
// 5. Product has been excluded from Rapid Delivery (badged with excludeFromRapidDelivery flag)
export function isProductPurchaseDisabled(hasDisplayPrice: boolean, availableForDayAtStore: boolean, isAddToCartDisabled: boolean, isExcludedFromRapidDelivery: boolean) {
  return !hasDisplayPrice || !availableForDayAtStore || isAddToCartDisabled === true || isExcludedFromRapidDelivery === true
}

//TODO Refactor to use testUserAgent and get rid of this function
// Function to detect browser
export function detectBrowser() {
  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent
    // Check if browser is Safari
    if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
      return 'Safari'
    }
    return 'Other'
  }
}

export const isMobile = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth <= 768
}

export const formatHyphenatedString = (str: string): string | undefined => {
  if (!str) return
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const getDisabledButtonText = (disabledStatus: string | null): string => {
  if (disabledStatus && typeof disabledStatus === 'string') {
    return formatHyphenatedString(disabledStatus) ?? 'Out of Stock'
  }
  return 'Out of Stock'
}

/**
 * Determines which text to show in an overlay for a product that is unavailable to purchase.
 * @param {Hit} hit 
 * @returns {string | undefined} The reason for the product being unavailable, or undefined
 */
export const getUnavailableProductReason = (hit: Hit): string | undefined => {
  if (hit.disabledStatus) {
    return hit.disabledStatus
  }
  if (hit.excludeFromRapidDelivery) {
    return 'exclude-from-rapid-delivery'
  }
  if (!hit.availableForDayAtStore) {
    return 'out-of-stock'
  }
  return undefined
}

function handleClickOutsideOfPopovers(
  event: MouseEvent | TouchEvent,
  isOpen: boolean,
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  popoverClassName: string
) {
  const target = event.target as HTMLElement
  // Close popover if the click is outside
  if (!target.closest(`.${popoverClassName}`) && isOpen) {
    setIsOpen(false)
  }
}

export function handleClosePopoverEvents(
  isOpen: boolean,
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  popoverClassName: string
) {
  document.addEventListener('mousedown', (e) => {
    handleClickOutsideOfPopovers(e, isOpen, setIsOpen, popoverClassName)
  })
  document.addEventListener('touchstart', (e) => {
    handleClickOutsideOfPopovers(e, isOpen, setIsOpen, popoverClassName)
  })

  return () => {
    document.removeEventListener('mousedown', (e) => {
      handleClickOutsideOfPopovers(e, isOpen, setIsOpen, popoverClassName)
    })
    document.removeEventListener('touchstart', (e) => {
      handleClickOutsideOfPopovers(e, isOpen, setIsOpen, popoverClassName)
    })
  }
}

// Encode ArrayBuffer to Hex
export function hexEncode(data: ArrayBuffer) {
  const hashArray = new Uint8Array(data)
  return Array.from(hashArray, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export async function asyncScriptLoader(scriptSrc: string, attributes?: Record<string, string>) {
  let scriptElement = document.querySelector(
    `script[src="${scriptSrc}"]`,
  ) as HTMLScriptElement | null

  // Function to create a promise that resolves on load and rejects on error.
  const waitForScript = (script: HTMLScriptElement) =>
    new Promise<void>((resolve, reject) => {
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true'
        resolve()
      })
      script.addEventListener('error', reject)
    })

  if (scriptElement) {
    // If the script has already been loaded, return immediately.
    if (scriptElement.dataset.loaded) {
      return
    }
    // Otherwise, wait for it to load.
    await waitForScript(scriptElement)
  } else {
    // Create and append the script element.
    scriptElement = document.createElement('script')
    scriptElement.src = scriptSrc
    scriptElement.async = true
    // Set attributes (If any)
    if (attributes) {
      for (const [key, value] of Object.entries(attributes)) {
        scriptElement.setAttribute(key, value)
      }
    }
    document.body.prepend(scriptElement)
    await waitForScript(scriptElement)
  }
}

/**
 * Function to test against user agent string, with redundancy for deprecated schema
 * @param regex Regular expression to test against in the user agent string
 * @returns true if a match is found
 */
export function testUserAgent(regex: RegExp): boolean {
  let uaString = ''
  if (typeof navigator !== 'undefined') {
    // @ts-ignore
    if (navigator.userAgentData && typeof navigator.userAgentData.platform === 'string') {
      // @ts-ignore
      uaString = navigator.userAgentData.platform + ' ' + (navigator.userAgent || '')
    } else if (navigator.userAgent) {
      uaString = navigator.userAgent
    }
  }
  return regex.test(uaString)
}

/**
 * provides Debounce functionality as a Promise. Timeout gets reset on every call to the debounce function
 * @param fn THe function to be debounced
 * @param time the delay before triggering the function
 * @returns
 */
export function debouncePromise<T extends (...args: any[]) => any>(fn: T, time: number) {
  let timer: NodeJS.Timeout | undefined = undefined

  return function debounced(...args: Parameters<T>) {
    if (timer) {
      clearTimeout(timer) // Clear the timeout first if it's already defined.
    }

    return new Promise<ReturnType<T>>((resolve) => {
      timer = setTimeout(() => resolve(fn(...args)), time)
    })
  }
}

/**
 * Detects the screensize of the current device
 * @returns a string with the correct device type
 */
export function detectScreenSize(): 'mobile' | 'tablet' | 'desktop' | undefined {
  if (typeof window === 'undefined') return
  const width = window.innerWidth
  if (width <= 767) {
    return 'mobile'
  } else if (width <= 1023) {
    return 'tablet'
  } else {
    return 'desktop'
  }
}

export const calculateDrawerWidth = (): number => {
  if (typeof window !== 'undefined') {
    const screenWidth = window.innerWidth
    const drawerWidth = Math.min(Math.max(screenWidth * 0.75, 240), 360)
    return drawerWidth
  }
  return 240
}

/**
 * Keeps track of the browsing position between pages
 */
export const retainBrowsingPosition = () => {
  if (!isServer) {
    window.history.replaceState({...window.history.state, scrollPositionY: window.scrollY}, '')
  }
}

/**
 *
 * Send provided data to the JS Bridge between the PWA and the app
 * @param bridgeRequest
 * @param routeName
 */
export const sendAppBridgeMessage = (
  bridgeRequest: Record<string, string | number | boolean>,
  routeName?: string,
) => {
  // Detect we're an Android or IOS device
  const app = new BonusCardApp()
  const androidRouteMapping = {'/account/order-history': 'selfService'}

  try {
    switch (app.platform?.platformName) {
      case 'Android':
        // Send the data to the App using the app interface if exists

        if (routeName && Object.keys(androidRouteMapping).includes(routeName)) {
          // Use a custom defined controller on android. If specified by the route key
          // @ts-ignore
          window.appInterface?.[androidRouteMapping[routeName]](JSON.stringify(bridgeRequest))
        }
        // Use the generic handler.
        // @ts-ignore
        window.appInterface?.success(JSON.stringify(bridgeRequest))
        break
      case 'iOS':
        // @ts-ignore
        window?.webkit?.messageHandlers?.message?.postMessage(bridgeRequest)
        break
    }
  } catch (e) {
    // Not in App, ignore
  }
}

export const numbersToWords = (num: number): string => {
  
  if (num === 0) return 'zero'

  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

  const parts: string[] = []

  // Handle hundreds
  const h = Math.floor(num / 100)
  if (h > 0) {
    parts.push(ones[h] + ' hundred')
  }

  // Handle tens and ones
  const remainder = num % 100
  if (remainder >= 10 && remainder < 20) {
    parts.push(teens[remainder - 10])
  } else {
    const t = Math.floor(remainder / 10)
    const o = remainder % 10
    
    if (t > 0) {
      parts.push(o > 0 ? tens[t] + '-' + ones[o] : tens[t])
    } else if (o > 0) {
      parts.push(ones[o])
    }
  }

  return parts.join(' ')
}

/**
 *  Combines the products to be added to the basket with the existing products in the basket, summing quantities for matching productIds
 * @param productsToAdd array of products to add to the basket
 * @param existingProducts array of existing products in the basket
 * @returns updated array of products to update basket
 */

type BasketLine = {productId: string, quantity: number}

export const combineProductsWithExistingBasket = (productsToAdd: BasketLine[], existingProducts: Array<{productId?: string, quantity?: number}>): BasketLine[] => {
  const updatedBasket: BasketLine[] = existingProducts
    .filter((item): item is {productId: string, quantity: number} => {
      return typeof item.productId === 'string' && typeof item.quantity === 'number'
    })
    .map((item) => ({productId: item.productId, quantity: item.quantity}))

  productsToAdd.forEach((product) => {
    const existingProductIndex = updatedBasket.findIndex((item) => item.productId === product.productId)
    if (existingProductIndex !== -1) {
      updatedBasket[existingProductIndex] = {
        ...updatedBasket[existingProductIndex],
        quantity: updatedBasket[existingProductIndex].quantity + product.quantity
      }
    } else {
      updatedBasket.push({
        productId: product.productId,
        quantity: product.quantity
      })
    }
  })

  return updatedBasket
}

export function formatPrice(amount: number | undefined,currency: string,): string {
  if (amount === undefined || isNaN(amount)) {
    return '-';
  }
  if (amount <= 0) {
    return 'FREE';
  }

  if(amount < 1) {
    return `${(amount * 100).toFixed(0)}p`
  }

  return new Intl.NumberFormat('en', {
  style: 'currency',
  currency,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  }).format(amount)
}

export function sortPromotionsByRank(promotions: Promotion[]) {
  if (!promotions || promotions.length === 0) return []
  if (promotions.length === 1) return promotions
  return promotions.sort((a, b) => {
    const aHasRank = a.rank !== undefined
    const bHasRank = b.rank !== undefined

    // Ranked promotions should appear before unranked promotions.
    if (aHasRank && !bHasRank) {
      return -1
    }
    if (!aHasRank && bHasRank) {
      return 1
    }

    // If both have rank, sort by lowest rank first.
    if (a.rank && b.rank && a.rank !== b.rank) {
      return a.rank - b.rank
    }

    // If both are unranked, or both share the same rank, newest first.
    const dateA = a.creationDate ? new Date(a.creationDate) : new Date(0)
    const dateB = b.creationDate ? new Date(b.creationDate) : new Date(0)
    return dateB.getTime() - dateA.getTime()
  })
}